import React, { useState, useEffect } from "react";
import ColoredCheckbox from "../../components/ColoredCheckbox/ColoredCheckbox";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import GeneralTable from "../../components/Table/GeneralTable";
import "./MarkAttendance.scss";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

function MarkAttendance() {
  const { user } = useAuth();

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getCurrentTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, "0")}:${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  };

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);

  const [date, setDate] = useState(getCurrentDate());
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [timeType, setTimeType] = useState("punchIn");
  const [timeValue, setTimeValue] = useState(getCurrentTime());

  const [selectAll, setSelectAll] = useState(false);
  const [roleFilter, setRoleFilter] = useState("invalid");

  // Fetch roles & attendance types (no dependency on user)
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/roles/all`,
        );
        setRoles(response.data.data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    const fetchAttendanceTypes = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance-types`,
        );
        setLocations(response.data.data);
      } catch (error) {
        console.error("Error fetching attendance types:", error);
      }
    };

    fetchRoles();
    fetchAttendanceTypes();
  }, []);

  // Fetch employees AFTER user loads (ADMIN FIRST)
  useEffect(() => {
    if (!user) return;

    const fetchEmployees = async () => {
      try {
        const authorityCodes = (user.authorities || []).map((a) =>
          typeof a === "string" ? a : a.code,
        );

        const isAdmin = authorityCodes.includes("ROLE_ADMIN");
        const isManager = authorityCodes.includes("ROLE_MANAGER");

        let endpoint = "";

        // ADMIN ALWAYS FIRST
        if (isAdmin) {
          endpoint = `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/All`;
        } else if (isManager) {
          endpoint = `${process.env.REACT_APP_API_MANAGER_URL}/employees/manager`;
        } else {
          toast.error("User has no employee-fetching authority");
          setEmployees([]);
          return;
        }

        const response = await axiosInstance.get(endpoint);

        setEmployees(
          response.data.data.map((emp) => ({
            id: emp.empId,
            name: `${emp.firstName} ${emp.lastName}`,
            roleId: emp.role?.roleId ?? null,
          })),
        );
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    fetchEmployees();
  }, [user]);

  // Role map
  const roleMap = roles.reduce((acc, role) => {
    acc[role.roleId] = role.roleName;
    return acc;
  }, {});

  // The shared table handles text search; keep only the role filter here so
  // select-all continues to apply to the whole role-filtered employee list.
  const filteredEmployees = employees.filter((emp) => {
    return roleFilter === "invalid" || roleMap[emp.roleId] === roleFilter;
  });

  // Employee selection handling
  const handleEmployeeChange = (e) => {
    const value = e.target.value;
    setSelectedEmployees((prev) =>
      prev.includes(value)
        ? prev.filter((id) => id !== value)
        : [...prev, value],
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map((e) => e.name));
    }
    setSelectAll(!selectAll);
  };

  const convertTo12HourFormat = (time24) => {
    if (!time24) return "";
    const [hours, minutes] = time24.split(":");
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12.toString().padStart(2, "0")}:${minutes} ${ampm}`;
  };

  // Submit attendance
  const handleSubmit = async (e) => {
    e.preventDefault();

    const employeeIds = selectedEmployees.map(
      (name) => employees.find((x) => x.name === name).id,
    );

    const authorities = user?.authorities || [];
    const isAdmin = authorities.includes("ROLE_ADMIN");
    const isManager = authorities.includes("ROLE_MANAGER");

    let apiUrl = "";
    if (isAdmin) {
      apiUrl = `${process.env.REACT_APP_API_BASE_URL}/attendance/team`;
    } else if (isManager) {
      apiUrl = `${process.env.REACT_APP_API_MANAGER_URL}/attendance/team`;
    }

    if (!apiUrl) {
      toast.error("Unauthorized role for marking attendance.");
      return;
    }

    if (!location) {
      toast.error("Please select location");
      return;
    }

    if (employeeIds.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }

    const requestBody = {
      employeeIds,
      attendanceDate: date,
      punchTime: convertTo12HourFormat(timeValue),
      punchType: timeType === "punchIn" ? "PunchIn" : "PunchOut",
      attendanceTypeId: location,
    };

    try {
      await axiosInstance.post(apiUrl, requestBody);

      setSelectedEmployees([]);
      setDate(getCurrentDate());
      setTimeValue(getCurrentTime());
      setLocation("");
      setSelectAll(false);

      toast.success("Attendance marked successfully!");
    } catch (error) {
      console.error("Error submitting attendance:", error);
    }
  };

  const SelectAllHeader = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <ColoredCheckbox
        color="green"
        checked={
          filteredEmployees.length > 0 &&
          selectedEmployees.length === filteredEmployees.length
        }
        onChange={handleSelectAllChange}
      />
      <span>Employee Names</span>
    </div>
  );

  const columns = [
    {
      header: <SelectAllHeader />,
      accessor: "employeeNameDisplay",
      renderHeader: SelectAllHeader,
    },
    { header: "Role", accessor: "role" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const tableData = filteredEmployees.map((emp) => ({
    id: emp.id,
    name: emp.name,
    role: roleMap[emp.roleId] || "N/A",
    employeeNameDisplay: (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <ColoredCheckbox
          checked={selectedEmployees.includes(emp.name)}
          onChange={handleEmployeeChange}
          value={emp.name}
          name="employee"
          color="green"
        />
        <span>{emp.name}</span>
      </div>
    ),
    searchableText: `${emp.name} ${roleMap[emp.roleId] || ""}`.toLowerCase(),
  }));

  return (
    <div className="mark-team">
      <h1 className="display6">Mark Team Attendance</h1>

      <div className="mark-attendance">
        {/* LEFT PANEL */}
        <div className="input-section">
          <div className="date-location-container">
            <div className="form-row">
              <select
                id="roleFilter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="invalid">All Roles</option>
                {roles.map((role) => (
                  <option key={role.roleId} value={role.roleName}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-group">
              <label>Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="location-group">
              <label>Location:</label>
              <select
                value={location}
                onChange={(e) => setLocation(Number(e.target.value))}
              >
                <option value="">Select Location</option>
                {locations.map((loc) => (
                  <option
                    key={loc.attendanceTypeId}
                    value={loc.attendanceTypeId}
                  >
                    {loc.attendanceType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="time-section">
            <div className="form-group">
              <label>Time Type</label>

              <div className="time-radios">
                <button
                  type="button"
                  className={`time-button ${
                    timeType === "punchIn" ? "active" : ""
                  }`}
                  onClick={() => setTimeType("punchIn")}
                >
                  Punch In
                </button>

                <button
                  type="button"
                  className={`time-button ${
                    timeType === "punchOut" ? "active" : ""
                  }`}
                  onClick={() => setTimeType("punchOut")}
                >
                  Punch Out
                </button>
              </div>
            </div>

            <div className="form-group time-input">
              <label htmlFor="timeValue">
                {timeType === "punchIn" ? "Punch In" : "Punch Out"} *
              </label>
              <input
                type="time"
                id="timeValue"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
              />
            </div>
          </div>

          <div className="submit-button-container">
            <ButtonWithIcon
              icon="mdi:send"
              text="Submit"
              onClick={handleSubmit}
              className="submit-button"
            />
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="filter-section">
          <div className="employee-checkboxes">
            <div className="general-table-container">
              <GeneralTable
                columns={columns}
                data={tableData}
                title="Employee List"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarkAttendance;
